import { z } from "zod";

import { DatabaseDefaults } from "@/src/models/database-defaults";
import { TaskType } from "@/src/models/task";

export enum VerificationResult {
  CORRECT = "correct",
  INCORRECT = "incorrect",
}

export enum VerificationReasonCode {
  DOCUMENT_METADATA_UPDATE = "document_metadata_update",
  DOCUMENT_NOT_UPLOADED = "document_not_uploaded",
  DUPLICATE_DOCUMENT_UPLOAD = "duplicate_document_upload",
  INCORRECT_ASSIGNMENT = "incorrect_assignment",
  INCORRECT_DEMOGRAPHIC_DATA = "incorrect_demographic_data",
  INCORRECT_INSURANCE = "incorrect_insurance",
  INCORRECT_PROVIDER = "incorrect_referring_provider",
  INCORRECT_SCHEDULING_NOTE = "incorrect_scheduling_note",
  INCORRECT_UPLOAD_LOCATION = "incorrect_upload_location",
  ORDER_MISMATCH = "order_mismatch",
  OTHER = "other",
  TEL_ENCOUNTER_ERROR = "tel_encounter_error",
}

export const VERIFICATION_REASON_LABELS: Record<VerificationReasonCode, string> = {
  [VerificationReasonCode.DOCUMENT_METADATA_UPDATE]: "Document metadata update",
  [VerificationReasonCode.DOCUMENT_NOT_UPLOADED]: "Document not uploaded",
  [VerificationReasonCode.DUPLICATE_DOCUMENT_UPLOAD]: "Duplicate document upload",
  [VerificationReasonCode.INCORRECT_ASSIGNMENT]: "Incorrect assignment",
  [VerificationReasonCode.INCORRECT_DEMOGRAPHIC_DATA]: "Demographic data",
  [VerificationReasonCode.INCORRECT_INSURANCE]: "Insurance",
  [VerificationReasonCode.INCORRECT_PROVIDER]: "Referring/receiving provider",
  [VerificationReasonCode.INCORRECT_SCHEDULING_NOTE]: "Scheduling note",
  [VerificationReasonCode.INCORRECT_UPLOAD_LOCATION]: "Incorrect upload location",
  [VerificationReasonCode.ORDER_MISMATCH]: "Order mismatch",
  [VerificationReasonCode.OTHER]: "Other",
  [VerificationReasonCode.TEL_ENCOUNTER_ERROR]: "Tel encounter error",
};

const DOCUMENT_VERIFICATION_REASONS: VerificationReasonCode[] = [
  VerificationReasonCode.DOCUMENT_NOT_UPLOADED,
  VerificationReasonCode.INCORRECT_ASSIGNMENT,
  VerificationReasonCode.INCORRECT_UPLOAD_LOCATION,
  VerificationReasonCode.TEL_ENCOUNTER_ERROR,
  VerificationReasonCode.DUPLICATE_DOCUMENT_UPLOAD,
  VerificationReasonCode.ORDER_MISMATCH,
  VerificationReasonCode.OTHER,
];

const REFERRAL_VERIFICATION_REASONS: VerificationReasonCode[] = [
  VerificationReasonCode.DOCUMENT_NOT_UPLOADED,
  VerificationReasonCode.DOCUMENT_METADATA_UPDATE,
  VerificationReasonCode.INCORRECT_DEMOGRAPHIC_DATA,
  VerificationReasonCode.INCORRECT_INSURANCE,
  VerificationReasonCode.INCORRECT_PROVIDER,
  VerificationReasonCode.INCORRECT_SCHEDULING_NOTE,
  VerificationReasonCode.OTHER,
];

export function getVerificationReasonsForTaskType(taskType: TaskType): VerificationReasonCode[] {
  switch (taskType) {
    case TaskType.REFERRAL:
      return REFERRAL_VERIFICATION_REASONS;
    case TaskType.DOC_MANAGEMENT:
    case TaskType.PRIOR_AUTHORIZATION:
      return DOCUMENT_VERIFICATION_REASONS;
    case TaskType.ELIGIBILITY_CHECK:
    case TaskType.REFERRAL_AUTHORIZATION:
    case TaskType.PATIENT_LOOKUP:
    case TaskType.SCHEDULE:
    case TaskType.WELCOME_CALL:
    case TaskType.LEAD_FORM:
    default:
      return [];
  }
}

export const taskVerificationSchema = z.object({
  taskId: z.string().uuid(),
  status: z.nativeEnum(VerificationResult),
  reason: z.string().nullable(),
  details: z.string().nullable(),
});

export type TaskVerification = z.infer<typeof taskVerificationSchema> & DatabaseDefaults;

export const taskVerificationShape: TaskVerification = {
  id: "",
  taskId: "",
  status: VerificationResult.CORRECT,
  reason: null,
  details: null,
  groupId: "",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};
